import {bookingLocationLabel} from '../../../utility/bookingLocation';
import success_loader from '../../../assets/success_loader.json';

const BOOKING_CONFIRMATION_STORAGE_PREFIX = 'coachfirst.booking-confirmation.';

export const getMembershipConfirmationInfo = ({membership_pack, membershipV2CreditSummary}) => {
  const isMembershipV2 =
    membership_pack?.membershipV2 === true ||
    membershipV2CreditSummary?.provider === 'membership_v2';

  return {
    isMembershipV2,
    name: membershipV2CreditSummary?.planName || membership_pack?.name || 'Membership',
  };
};

export const formatBookingAmount = value => {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : null;
};

export const isValidBookingConfirmationPageData = confirmationData =>
  Boolean(confirmationData?.basic?.startDateTime && confirmationData?.basic?.bookingType);

export const getBookingConfirmationPath = appointmentId =>
  appointmentId
    ? `/booking-confirmation/${encodeURIComponent(appointmentId)}`
    : '/booking-confirmation';

export const storeBookingConfirmationPageData = (appointmentId, confirmationData, storage) => {
  if (!appointmentId || !isValidBookingConfirmationPageData(confirmationData)) return false;

  try {
    const confirmationStorage =
      storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!confirmationStorage) return false;

    confirmationStorage.setItem(
      `${BOOKING_CONFIRMATION_STORAGE_PREFIX}${appointmentId}`,
      JSON.stringify(confirmationData)
    );
    return true;
  } catch (error) {
    console.log('Unable to store booking confirmation data', error);
    return false;
  }
};

export const getStoredBookingConfirmationPageData = (appointmentId, storage) => {
  if (!appointmentId) return null;

  try {
    const confirmationStorage =
      storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (!confirmationStorage) return null;

    const storedValue = confirmationStorage.getItem(
      `${BOOKING_CONFIRMATION_STORAGE_PREFIX}${appointmentId}`
    );
    if (!storedValue) return null;

    const confirmationData = JSON.parse(storedValue);
    return isValidBookingConfirmationPageData(confirmationData) ? confirmationData : null;
  } catch (error) {
    console.log('Unable to restore booking confirmation data', error);
    return null;
  }
};

export const getBookingConfirmationPageData = ({
  order,
  service,
  subscription,
  member,
  membership_pack,
  ptRemaining,
  doNotShowOrder,
  bookedWithCredits,
  isUnlimited,
  isFromCheckIn,
  nextInstallments,
  membershipV2CreditSummary,
}) => {
  try {
    // const isSquare = service?.payment_method === "square";
    const isClass = service?.typename !== 'appointment';
    let participants = [
      [service?.memberFirstName || member?.firstName, service?.memberLastName || member?.lastName]
        .filter(Boolean).join(' '),
    ].filter(Boolean);
    let coaches = [service?.trainerName];
    if (isClass) {
      participants = service?.familyMembers?.length
        ? service.familyMembers.map(e => e?.isParent
            ? `${member?.firstName || ''} ${member?.lastName || ''}`
            : `${e?.firstName || ''} ${e?.lastName || ''}`)
        : participants;
      coaches = service?.selectedTrainers?.length
        ? service.selectedTrainers.map(e => e?.trainerName)
        : coaches;
    }
    const total = formatBookingAmount(
      order?.discountedPrice ?? order?.price ?? subscription?.price
    );
    const originalPrice = formatBookingAmount(order?.originalPrice);
    const discountAmount = formatBookingAmount(order?.discountAmount);
    const hasDiscount =
      originalPrice !== null &&
      discountAmount !== null &&
      Number(discountAmount) > 0 &&
      total !== null;
    const normalizedMembershipPack =
      membership_pack ||
      (membershipV2CreditSummary
        ? {
            membershipV2: true,
            name: membershipV2CreditSummary?.planName || 'Membership',
            isQuantityUnlimited: membershipV2CreditSummary?.unlimited === true,
          }
        : null);
    const response = {
      basic: {
        startDateTime: service?.start_branch_time,
        endDateTime: service?.end_branch_time,
        serviceName: service?.serviceName,
        coaches,
        participants,
        location: bookingLocationLabel(service),
        savedLocation: service?.savedLocation,
        zoomLink: service?.zoomLink,
        bookingType: isClass ? (order?.isCamp ? 'camp' : 'class') : 'appointment',
        timezone: service?.timezone,
      },
      remaining: ptRemaining,
      membership_pack: normalizedMembershipPack,
      membershipV2CreditSummary,
      doNotShowOrder: doNotShowOrder,
      bookedWithCredits: bookedWithCredits,
      isUnlimited: membershipV2CreditSummary?.unlimited ?? isUnlimited,
      isFromCheckIn,
      nextInstallments: nextInstallments,
      order: {
        // servicePrice: service?.price,
        // cardProcessingFee:
        //   isSquare &&
        //   order?.square_payment_response?.app_fee_money?.amount / 100,
        // platformFee: isSquare && (service?.price * 2.99) / 100 + 30 / 100,
        isShow: !doNotShowOrder && total !== null,
        total,
        ...(hasDiscount
          ? {
              hasDiscount: true,
              originalPrice,
              discountAmount,
            }
          : {}),
        // orderId: service?.orderId,
        // receiptUrl: isSquare && order?.receipt_url,
      },
    };
    return response;
  } catch (error) {
    console.log('error', error, error.response);
  }
};

export const getBookingConfirmationPageDataFromAppointmentDetails = ({
  appointment,
  persistedData,
}) => {
  if (!appointment) return null;

  const persistedTotal = formatBookingAmount(persistedData?.order?.total);
  const persistedOrder =
    persistedTotal !== null
      ? {
          price: persistedTotal,
          originalPrice: persistedData?.order?.originalPrice,
          discountAmount: persistedData?.order?.discountAmount,
          discountedPrice: persistedData?.order?.total,
        }
      : null;
  const order =
    appointment?.orderDetails && persistedOrder
      ? {...persistedOrder, ...appointment.orderDetails}
      : appointment?.orderDetails || persistedOrder;
  const membershipV2CreditSummary =
    appointment?.membershipV2CreditSummary || persistedData?.membershipV2CreditSummary;
  const doNotShowOrder = persistedData?.doNotShowOrder ?? true;
  const bookedWithCredits =
    persistedData?.bookedWithCredits ??
    (appointment?.payment_method === 'membership_v2' || Boolean(membershipV2CreditSummary));

  const confirmationData = getBookingConfirmationPageData({
    order,
    service: appointment,
    membership_pack: persistedData?.membership_pack,
    ptRemaining: persistedData?.remaining,
    doNotShowOrder,
    bookedWithCredits,
    isUnlimited: persistedData?.isUnlimited,
    isFromCheckIn: persistedData?.isFromCheckIn,
    nextInstallments: persistedData?.nextInstallments,
    membershipV2CreditSummary,
  });

  if (!confirmationData) return null;

  return {
    ...confirmationData,
    order: {
      ...confirmationData.order,
      total: confirmationData.order?.total ?? persistedTotal,
      isShow:
        !doNotShowOrder &&
        formatBookingAmount(confirmationData.order?.total ?? persistedTotal) !== null,
    },
  };
};

export const postBookingLottieDefaultOptions = {
  loop: true,
  autoplay: true,
  animationData: success_loader,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};
